import { useI18n } from 'domains/i18n/hooks'
import { useAppDispatch } from 'domains/store'
import {
  doneProcessingImage,
  registerUpload,
  setUploadComplete,
  setUploadError,
  setUploadProgress,
  startProcessingImage,
} from 'domains/store/slice'
import { randomId } from 'lib/id'
import { useCallback } from 'preact/hooks'
import SeamlyFileUploadContext from 'ui/components/core/seamly-file-upload-context'
import { useSeamlyApiContext, useSeamlyCommands } from 'ui/hooks/seamly-hooks'

const calculateImgSize = (img) => {
  const MAX_WIDTH = 600
  const MAX_HEIGHT = 600

  const { height, width } = img
  if (width > height && width > MAX_WIDTH) {
    return [MAX_WIDTH, (height * MAX_WIDTH) / width]
  }

  if (height > MAX_HEIGHT) {
    return [(width * MAX_HEIGHT) / height, MAX_HEIGHT]
  }

  return [width, height]
}

const resizeImage = (img) => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  const [width, height] = calculateImgSize(img)
  canvas.width = width
  canvas.height = height

  ctx?.drawImage(img, 0, 0, width, height)

  return canvas.toDataURL()
}

const toBase64 = (file: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      }
    }
    reader.onerror = (error) => reject(error)
  })

const SeamlyFileUpload = ({ children }) => {
  const { t } = useI18n()
  const dispatch = useAppDispatch()
  const api = useSeamlyApiContext()
  const { addUploadBubble } = useSeamlyCommands()

  const addImageToSessionStorage = useCallback(
    async (file, fileId) => {
      dispatch(startProcessingImage(fileId))
      const base64 = await toBase64(file)
      const img = new Image()

      img.src = base64

      await img.decode()
      const newDataUri = resizeImage(img)

      try {
        sessionStorage.setItem(`image-${fileId}`, newDataUri)
      } catch (_error) {
        // Nothing to do!
      } finally {
        dispatch(doneProcessingImage(fileId))
      }
    },
    [dispatch],
  )

  const onUploadFileHandler = useCallback(
    (file) => {
      const fileId = randomId()

      const uploadHandle = api.uploadFile(
        file,
        (p) => {
          dispatch(setUploadProgress({ fileId, progress: Math.ceil(p) }))
        },
        async (result) => {
          const {
            id,
            transactionId,
            occurredAt,
            body: { contentType, filename, filesize },
          } = result
          dispatch(setUploadComplete(fileId))
          addUploadBubble(
            id,
            transactionId,
            occurredAt,
            contentType,
            filename,
            filesize,
          )

          if (contentType.includes('image/')) {
            await addImageToSessionStorage(file, id)
          }
        },
        (err) => {
          const errorKey = err?.error || ''
          let errorText: string

          switch (errorKey) {
            case 'file_uploads_are_disabled':
              errorText = t('fileUpload.errors.unavailable')
              break
            case 'request_entity_too_large':
              errorText = t('fileUpload.errors.tooLarge')
              break
            case 'file_has_invalid_mime_type':
              errorText = t('fileUpload.errors.wrongType')
              break
            case 'virus_found':
              errorText = t('fileUpload.errors.virusFound')
              break
            default:
              errorText = t('fileUpload.errors.general')
          }

          dispatch(setUploadError({ fileId, errorText }))
        },
      )

      dispatch(
        registerUpload({
          fileId,
          fileName: file.name,
          uploadHandle,
        }),
      )
    },
    [addImageToSessionStorage, addUploadBubble, api, dispatch, t],
  )

  return (
    <SeamlyFileUploadContext.Provider value={onUploadFileHandler}>
      {children}
    </SeamlyFileUploadContext.Provider>
  )
}

export default SeamlyFileUpload
